/**
 * Utility functions for parsing and organizing booking history data
 */

/**
 * Parse booking_history JSON string
 */
export const parseBookingHistory = (bookingHistoryStr) => {
  if (!bookingHistoryStr) return null;
  try {
    return JSON.parse(bookingHistoryStr);
  } catch (e) {
    console.error("Failed to parse booking_history:", e);
    return null;
  }
};

/**
 * Check if a record has booking history
 */
export const hasBookingHistory = (record) => {
  return !!record.booking_history;
};

/**
 * Group booking records by booking attempts
 * A booking attempt is identified by a unique combination of check-in/check-out dates
 */
export const groupBookingRecordsByAttempts = (records) => {
  const bookingRecords = records.filter(hasBookingHistory);
  if (bookingRecords.length === 0) return [];

  // Parse all booking records
  const parsedRecords = bookingRecords
    .map((record) => {
      const parsed = parseBookingHistory(record.booking_history);
      if (!parsed) return null;
      return {
        ...record,
        bookingData: parsed,
      };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(a.msg_time) - new Date(b.msg_time)); // Sort by time

  // Group by booking attempts (unique check-in/check-out date combinations)
  const attemptsMap = new Map();

  parsedRecords.forEach((record) => {
    const { action, data } = record.bookingData;
    let attemptKey = null;

    // Get dates from different actions
    if (action === "dates_selected" && data.checkin_date && data.checkout_date) {
      attemptKey = `${data.checkin_date}_${data.checkout_date}`;
    } else if (action === "rooms_presented" && data.checkin_date && data.checkout_date) {
      attemptKey = `${data.checkin_date}_${data.checkout_date}`;
    } else if (action === "booking_confirmed" && data.checkin_date && data.checkout_date) {
      attemptKey = `${data.checkin_date}_${data.checkout_date}`;
    } else if (action === "room_selected" || action === "occupancy_selected" || action === "offer_selected") {
      // For these actions, we need to find the most recent dates_selected or rooms_presented
      // Look backwards in the parsed records to find the dates
      for (let i = parsedRecords.indexOf(record) - 1; i >= 0; i--) {
        const prevRecord = parsedRecords[i];
        const prevAction = prevRecord.bookingData.action;
        const prevData = prevRecord.bookingData.data;
        
        if (prevAction === "dates_selected" || prevAction === "rooms_presented" || prevAction === "booking_confirmed") {
          if (prevData.checkin_date && prevData.checkout_date) {
            attemptKey = `${prevData.checkin_date}_${prevData.checkout_date}`;
            break;
          }
        }
      }
    }

    // If we couldn't find a key, create a unique one based on timestamp
    if (!attemptKey) {
      attemptKey = `unknown_${record.timestamp}`;
    }

    if (!attemptsMap.has(attemptKey)) {
      attemptsMap.set(attemptKey, {
        attemptKey,
        records: [],
        checkinDate: null,
        checkoutDate: null,
        guestInfo: null,
      });
    }

    const attempt = attemptsMap.get(attemptKey);
    attempt.records.push(record);

    // Update dates if we have them
    if (action === "dates_selected" || action === "rooms_presented" || action === "booking_confirmed") {
      if (data.checkin_date) attempt.checkinDate = data.checkin_date;
      if (data.checkout_date) attempt.checkoutDate = data.checkout_date;
    }

    // Update guest info from dates_selected
    if (action === "dates_selected" && data.user_first_name) {
      attempt.guestInfo = {
        firstName: data.user_first_name,
        lastName: data.user_last_name || "",
        email: data.user_email || "",
        phone: data.user_phone || "",
      };
    }

    // Update guest info from booking_confirmed if not already set
    if (action === "booking_confirmed" && data.user_details && !attempt.guestInfo) {
      attempt.guestInfo = {
        firstName: data.user_details.first_name || "",
        lastName: data.user_details.last_name || "",
        email: data.user_details.email || "",
        phone: data.user_details.phone || "",
      };
    }
  });

  // Convert map to array and sort by most recent first
  const attempts = Array.from(attemptsMap.values())
    .map((attempt) => {
      // Sort records within attempt by time (oldest first)
      attempt.records.sort((a, b) => new Date(a.msg_time) - new Date(b.msg_time));
      
      // Get the earliest timestamp for sorting attempts
      attempt.earliestTime = attempt.records[0]?.msg_time;
      
      return attempt;
    })
    .sort((a, b) => new Date(b.earliestTime) - new Date(a.earliestTime)); // Most recent first

  return attempts;
};

/**
 * Get booking attempt status
 */
export const getBookingAttemptStatus = (attempt) => {
  const records = attempt.records || [];
  
  // Check for booking_confirmed
  const hasConfirmed = records.some(
    (r) => r.bookingData?.action === "booking_confirmed"
  );
  if (hasConfirmed) {
    return {
      type: "confirmed",
      label: "Booking Confirmed",
      color: "success",
    };
  }

  // Check for rooms_presented
  const roomsPresented = records.find(
    (r) => r.bookingData?.action === "rooms_presented"
  );
  if (roomsPresented) {
    const roomsAvailable = roomsPresented.bookingData?.data?.rooms_available;
    if (roomsAvailable === false) {
      return {
        type: "no_availability",
        label: "No Availability",
        color: "danger",
      };
    } else if (roomsAvailable === true) {
      return {
        type: "rooms_available",
        label: "Rooms Available",
        color: "success",
      };
    }
  }

  // Check for offer_selected
  const hasOfferSelected = records.some(
    (r) => r.bookingData?.action === "offer_selected"
  );
  if (hasOfferSelected) {
    return {
      type: "offer_selected",
      label: "Offer Selected",
      color: "warning",
    };
  }

  // Check for dates_selected
  const hasDatesSelected = records.some(
    (r) => r.bookingData?.action === "dates_selected"
  );
  if (hasDatesSelected) {
    return {
      type: "dates_selected",
      label: "Date Selected",
      color: "info",
    };
  }

  return {
    type: "unknown",
    label: "In Progress",
    color: "secondary",
  };
};

/**
 * Format date for display
 */
export const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    // Parse date string as local date to avoid timezone issues
    // If dateStr is in format "YYYY-MM-DD" (date only, not datetime), parse it as local date
    if (typeof dateStr === "string") {
      // Check if it's a pure date string (YYYY-MM-DD) without time component
      const dateOnlyMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateOnlyMatch) {
        const [, year, month, day] = dateOnlyMatch.map(Number);
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      }
      // For ISO datetime strings (including UTC), extract date part from the string itself
      // This ensures we use the date as it appears in the string, not after timezone conversion
      const isoDateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})T/);
      if (isoDateMatch) {
        // Extract the date components directly from the string to avoid timezone issues
        const [, year, month, day] = isoDateMatch.map(Number);
        // Create date in local timezone using the extracted date parts
        // This ensures Dec 23 stays Dec 23 regardless of timezone
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      }
    }
    // For other formats, try to parse and extract date components
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    // Use UTC date components to avoid timezone shifts
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();
    const localDate = new Date(year, month, day);
    return localDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (e) {
    return "Invalid Date";
  }
};

/**
 * Format time for display (e.g., "03:18 PM")
 */
export const formatDisplayTime = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return "Invalid Time";
    }
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (e) {
    return "Invalid Time";
  }
};

/**
 * Calculate number of nights
 */
export const calculateNights = (checkinDate, checkoutDate) => {
  if (!checkinDate || !checkoutDate) return 0;
  try {
    // Parse dates as local dates to avoid timezone issues
    let checkin, checkout;
    
    if (typeof checkinDate === "string" && /^\d{4}-\d{2}-\d{2}/.test(checkinDate)) {
      const [year, month, day] = checkinDate.split("-").map(Number);
      checkin = new Date(year, month - 1, day);
    } else {
      checkin = new Date(checkinDate);
    }
    
    if (typeof checkoutDate === "string" && /^\d{4}-\d{2}-\d{2}/.test(checkoutDate)) {
      const [year, month, day] = checkoutDate.split("-").map(Number);
      checkout = new Date(year, month - 1, day);
    } else {
      checkout = new Date(checkoutDate);
    }
    
    const diffTime = checkout - checkin;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  } catch (e) {
    return 0;
  }
};

/**
 * Group booking records by date combinations (new approach)
 * Each unique check-in/check-out date combination creates a new group
 * Actions without dates are assigned to the most recent group
 */
export const groupBookingRecordsByDateCombinations = (records) => {
  const bookingRecords = records.filter(hasBookingHistory);
  if (bookingRecords.length === 0) return [];

  // Parse all booking records and sort chronologically
  const parsedRecords = bookingRecords
    .map((record) => {
      const parsed = parseBookingHistory(record.booking_history);
      if (!parsed) return null;
      return {
        ...record,
        bookingData: parsed,
      };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(a.msg_time) - new Date(b.msg_time)); // Oldest first

  // Track groups and current active group
  const groupsMap = new Map();
  let currentGroupKey = null;
  let groupCounter = 0;

  parsedRecords.forEach((record) => {
    const { action, data } = record.bookingData;
    let groupKey = null;

    // Skip booking_intent_triggered - ignore it completely for now
    if (action === "booking_intent_triggered") {
      return; // Skip this record, don't add it to any group
    } else if (action === "offer_selected") {
      // offer_selected doesn't have dates; assign to current group
      const currentRecordIndex = parsedRecords.indexOf(record);
      for (let i = currentRecordIndex - 1; i >= 0; i--) {
        const prevRecord = parsedRecords[i];
        const prevAction = prevRecord.bookingData.action;
        const prevData = prevRecord.bookingData.data;
        if (prevAction === "booking_intent_triggered") continue;
        if (prevAction === "dates_selected" && prevData.checkin_date && prevData.checkout_date) {
          groupKey = `${prevData.checkin_date}_${prevData.checkout_date}`;
          currentGroupKey = groupKey;
          break;
        }
        if (!groupKey &&
            (prevAction === "rooms_presented" || prevAction === "booking_confirmed") &&
            prevData.checkin_date && prevData.checkout_date) {
          groupKey = `${prevData.checkin_date}_${prevData.checkout_date}`;
          currentGroupKey = groupKey;
        }
      }
      if (!groupKey && currentGroupKey) {
        groupKey = currentGroupKey;
      }
    } else if (action === "dates_selected" && data.checkin_date && data.checkout_date) {
      // New dates selected - create or find group with these dates
      // This starts a NEW booking attempt, so update currentGroupKey
      groupKey = `${data.checkin_date}_${data.checkout_date}`;
      currentGroupKey = groupKey;
    } else if (action === "rooms_presented" && data.checkin_date && data.checkout_date) {
      // Rooms presented with dates - create or find group with these dates
      groupKey = `${data.checkin_date}_${data.checkout_date}`;
      currentGroupKey = groupKey;
    } else if (action === "booking_confirmed" && data.checkin_date && data.checkout_date) {
      // Booking confirmed with dates - use these dates
      groupKey = `${data.checkin_date}_${data.checkout_date}`;
      currentGroupKey = groupKey;
    } else {
      // For actions without dates (room_selected, occupancy_selected, etc.)
      // Look backwards to find the most recent dates_selected action
      // This tells us which booking attempt this action belongs to
      const currentRecordIndex = parsedRecords.indexOf(record);
      for (let i = currentRecordIndex - 1; i >= 0; i--) {
        const prevRecord = parsedRecords[i];
        const prevAction = prevRecord.bookingData.action;
        const prevData = prevRecord.bookingData.data;

        // Skip booking_intent_triggered when looking backwards (it doesn't have dates)
        if (prevAction === "booking_intent_triggered") {
          continue;
        }

        // Priority 1: Find the most recent dates_selected - this definitively starts a booking attempt
        if (prevAction === "dates_selected" && prevData.checkin_date && prevData.checkout_date) {
          groupKey = `${prevData.checkin_date}_${prevData.checkout_date}`;
          currentGroupKey = groupKey;
          break;
        }

        // Priority 2: If no dates_selected found yet, use rooms_presented or booking_confirmed with dates
        // But continue searching in case there's a dates_selected further back
        if (!groupKey && 
            (prevAction === "rooms_presented" || prevAction === "booking_confirmed") &&
            prevData.checkin_date && prevData.checkout_date) {
          groupKey = `${prevData.checkin_date}_${prevData.checkout_date}`;
          currentGroupKey = groupKey;
          // Don't break - continue searching for dates_selected
        }
      }
      
      // If we still don't have a groupKey but have a currentGroupKey, use it
      if (!groupKey && currentGroupKey) {
        groupKey = currentGroupKey;
      }
    }

    // If still no group key, create a fallback group
    if (!groupKey) {
      groupKey = `unknown_${++groupCounter}`;
      currentGroupKey = groupKey;
    }

    // Create or get group
    if (!groupsMap.has(groupKey)) {
      groupsMap.set(groupKey, {
        groupKey,
        records: [],
        checkinDate: null,
        checkoutDate: null,
        earliestTime: record.msg_time,
        latestTime: record.msg_time,
      });
    }

    const group = groupsMap.get(groupKey);
    group.records.push(record);

    // Update dates if available
    if (data.checkin_date) group.checkinDate = data.checkin_date;
    if (data.checkout_date) group.checkoutDate = data.checkout_date;

    // Update timestamps - compare using UTC milliseconds to avoid timezone issues
    const recordTime = new Date(record.msg_time).getTime();
    const earliestTime = new Date(group.earliestTime).getTime();
    const latestTime = new Date(group.latestTime).getTime();
    
    if (recordTime < earliestTime) {
      group.earliestTime = record.msg_time;
    }
    if (recordTime > latestTime) {
      group.latestTime = record.msg_time;
    }
  });

  // Convert to array, sort records within each group, then sort groups by most recent first
  const groups = Array.from(groupsMap.values())
    .map((group) => {
      // Sort records within group chronologically
      group.records.sort((a, b) => new Date(a.msg_time) - new Date(b.msg_time));
      return group;
    })
    .sort((a, b) => new Date(b.earliestTime) - new Date(a.earliestTime)); // Most recent first

  return groups;
};

/**
 * Get group status (completed or incomplete)
 */
export const getGroupStatus = (group) => {
  const hasConfirmed = group.records.some(
    (r) => r.bookingData?.action === "booking_confirmed"
  );

  if (hasConfirmed) {
    return {
      status: "completed",
      label: "Completed",
      color: "success",
    };
  }

  return {
    status: "incomplete",
    label: "Incomplete",
    color: "warning",
  };
};

/**
 * Mask email address - show only the part before "@", mask the domain
 * Example: "john.doe@example.com" -> "john.doe@***"
 */
export const maskEmail = (email) => {
  if (!email || typeof email !== "string") return email || "N/A";
  
  const atIndex = email.indexOf("@");
  if (atIndex === -1) {
    // No "@" found, return as is
    return email;
  }
  
  const localPart = email.substring(0, atIndex);
  return `${localPart}@***`;
};

/**
 * Mask phone number - show only first 3 digits, mask the rest with ***
 * Example: "1234567890" -> "123***"
 */
export const maskPhone = (phone) => {
  if (!phone || typeof phone !== "string") return phone || "N/A";
  
  // Remove any non-digit characters for processing
  const digitsOnly = phone.replace(/\D/g, "");
  
  if (digitsOnly.length === 0) {
    return phone; // Return original if no digits found
  }
  
  if (digitsOnly.length <= 3) {
    // If 3 or fewer digits, show all
    return digitsOnly;
  }
  
  // Show first 3 digits, mask the rest
  return `${digitsOnly.substring(0, 3)}***`;
};

/**
 * Count booking_intent_triggered actions in records
 */
export const countBookingIntentTriggered = (records) => {
  const bookingRecords = records.filter(hasBookingHistory);
  if (bookingRecords.length === 0) return 0;

  let count = 0;
  bookingRecords.forEach((record) => {
    const parsed = parseBookingHistory(record.booking_history);
    if (parsed && parsed.action === "booking_intent_triggered") {
      count++;
    }
  });

  return count;
};

